import { useMemo } from 'react'
import { AppMenu as AppMenuLanguage } from '../../../i18n'
import { MainMenuApp, MainMenuAppTransfer } from '../../NonReact/constants/AppMenuConstants'
import { RxJSCurrentState, useRxJSState } from '../../React/Common/Hooks/useRxJSState'
import { appMenuUpdate$ } from '../../NonReact/common/RxJS/AppMenuState'
import { useCurrentPage } from './useCurrentPage'
import { useIsInsightsAvailable } from './useIsInsightsAvailable'
import { useIsPresenceAvailable } from './useIsPresenceAvailable'
import { useCheckUserHasPermissions } from './useCheckPermissions'
import { useUserHasSelfProvisioningPermission } from '../../React/Common/Hooks/permissions/useUserHasSelfProvisioningPermission'
import { useUserHasManageQueuePermission } from '../../React/Common/Hooks/permissions/useUserHasManageQueuesPermission'
import { useUserHasUserManagementPermission } from '../../React/Common/Hooks/permissions/useUserHasUserManagementPermission'
import { UserInfo, useUserInfo } from './useUserInfo'
import { AppMenuEntry, RouteID } from '../Components/Molecules/Header/Types/Types'
import { IconName } from '../../React/Common/Components/Icon'
import { Or, IsVarEmpty, isVarNotEmpty } from '../../NonReact/utils/GlobalFunctions'
import { ADMIN_MENU_PERMISSIONS, STATUS_AND_TOOLS_PERMISSIONS } from '../../React/Common/Utils/PermissionsUtil'
import { ReportingAccessLevels } from '../../NonReact/constants/Permissions'
import CLanguageHandler from '../../NonReact/Languages/LanguageHandler'
import { Page } from '../../NonReact/constants/Components/Page'

/**
 * Menu entries and selected menu state for the app launcher.
 */
export type UseAppMenuEntriesResult = {
    appMenuEntries: Definable<AppMenuEntry[]>
    appMenuState: RxJSCurrentState<MainMenuAppTransfer>
    showAppMenu: boolean
}

/**
 * Builds the app-launcher entries available to the current user.
 *
 * The hook combines route state, legacy JSP page state, user permissions, and
 * feature availability so React and non-React header surfaces can share the
 * same app menu behavior.
 *
 * @param routeID The current React route, when the caller is rendering inside the router.
 * @returns Available menu entries, the current app menu state, and whether the launcher should be shown.
 */
export function useAppMenuEntries(routeID?: Definable<RouteID>): UseAppMenuEntriesResult {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(AppMenuLanguage.NAME)

    const [userInfo]: UserInfo = useUserInfo()

    const currentJSPPage: Definable<Page> = useCurrentPage()

    const hasReportingAccess: Definable<boolean> = useMemo((): Definable<boolean> => {
        if (IsVarEmpty(userInfo)) return undefined
        return userInfo?.Scope?.ReportingAccess !== ReportingAccessLevels.NONE
    }, [userInfo])

    const isInsightsAvailable: Definable<boolean> = useIsInsightsAvailable()
    const isPresenceAvailable: Definable<boolean> = useIsPresenceAvailable()
    const isBroadworksProvisioningWizardAvailable: Definable<boolean> = useUserHasSelfProvisioningPermission()
    const isQueueManagementAvailable: Definable<boolean> = useUserHasManageQueuePermission()
    const isUserManagementAvailable: Definable<boolean> = useUserHasUserManagementPermission()
    const isAdminAvailable: Definable<boolean> = useCheckUserHasPermissions(ADMIN_MENU_PERMISSIONS, true)
    const isStatusAndToolsAvailable: Definable<boolean> = useCheckUserHasPermissions(STATUS_AND_TOOLS_PERMISSIONS, true)

    const isReportingAvailable: Definable<boolean> = useMemo(
        (): Definable<boolean> => Or(hasReportingAccess, isInsightsAvailable, isPresenceAvailable),
        [hasReportingAccess, isInsightsAvailable, isPresenceAvailable]
    )

    const isFastProvisioningAvailable: Definable<boolean> = useMemo(
        (): Definable<boolean> =>
            Or(isBroadworksProvisioningWizardAvailable, isQueueManagementAvailable, isUserManagementAvailable),
        [isBroadworksProvisioningWizardAvailable, isQueueManagementAvailable, isUserManagementAvailable]
    )

    const appMenuEntries: Definable<AppMenuEntry[]> = useMemo((): Definable<AppMenuEntry[]> => {
        if (
            isVarNotEmpty(isReportingAvailable) &&
            isVarNotEmpty(isFastProvisioningAvailable) &&
            isVarNotEmpty(isAdminAvailable) &&
            isVarNotEmpty(isStatusAndToolsAvailable)
        ) {
            return [
                {
                    enabled: isReportingAvailable,
                    title: language.get(AppMenuLanguage.AppMenu_Apps_Reporting),
                    icon: IconName.REPORTING,
                    menu: MainMenuApp.REPORTING,
                    pages: [RouteID.REPORTING_PORTAL, RouteID.INSIGHTS, RouteID.PRESENCE, RouteID.CXSCORE],
                    oldPages: [Page.REPORTING, Page.PRESENCE, Page.INSIGHTS_HUB]
                },
                {
                    enabled: isFastProvisioningAvailable,
                    title: language.get(AppMenuLanguage.AppMenu_Apps_FastProvisioning),
                    icon: IconName.FAST_PROVISIONING,
                    menu: MainMenuApp.FAST_PROVISIONING,
                    pages: [RouteID.LICENCE_MANAGEMENT]
                },
                {
                    enabled: isAdminAvailable,
                    title: language.get(AppMenuLanguage.AppMenu_Apps_Administration),
                    icon: IconName.ADMINISTRATION,
                    menu: MainMenuApp.ADMIN,
                    pages: [RouteID.CLIENT_CONFIGURATION, RouteID.CRM_INTEGRATION],
                    oldPages: [
                        Page.ADMIN_DEVICES,
                        Page.ADMIN_AGENTS,
                        Page.ADMIN_PARTITIONS,
                        Page.ADMIN_TELSYSS,
                        Page.ADMIN_CMENVIRONMENTS,
                        Page.ADMIN_USERS,
                        Page.ADMIN_CODES,
                        Page.ADMIN_CALLRECINTS,
                        Page.ADMIN_MESSAGES,
                        Page.ADMIN_DATABASE,
                        Page.ADMIN_DIRECTORY
                    ]
                },
                {
                    enabled: isStatusAndToolsAvailable,
                    title: language.get(AppMenuLanguage.AppMenu_Apps_StatusAndTools),
                    icon: IconName.STATUS_TOOLS,
                    menu: MainMenuApp.STATUS,
                    oldPages: [
                        Page.LICENSEMGR,
                        Page.LOGFILEMGR,
                        Page.ADMIN_LOGGING,
                        Page.TELMODULE_CALLCTRL,
                        Page.TELMODULE_STATUS,
                        Page.APP_STATUS,
                        Page.AUDITTRAIL
                    ]
                }
            ]
        }

        return []
    }, [
        isReportingAvailable,
        isFastProvisioningAvailable,
        isAdminAvailable,
        isStatusAndToolsAvailable,
        language.getLanguageID()
    ])

    const currentPage: Definable<MainMenuApp> = useMemo(
        (): Definable<MainMenuApp> =>
            (
                appMenuEntries?.find(
                    (entry: AppMenuEntry): boolean =>
                        !!(
                            (routeID && entry.pages?.includes(routeID)) ||
                            (currentJSPPage && entry.oldPages?.includes(currentJSPPage))
                        )
                ) ?? appMenuEntries?.[0]
            )?.menu,
        [appMenuEntries, routeID, currentJSPPage]
    )

    const appCount: number = useMemo(
        (): number => appMenuEntries?.filter(({ enabled }: AppMenuEntry): boolean => enabled)?.length ?? 0,
        [appMenuEntries]
    )

    const showAppMenu: boolean = appCount > 1

    const appMenuState: RxJSCurrentState<MainMenuAppTransfer> = useRxJSState(appMenuUpdate$, {
        app: currentPage ?? MainMenuApp.REPORTING,
        showAppMenu
    })

    return { appMenuEntries, appMenuState, showAppMenu }
}
